import { globalStores } from '@/editor/stores';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { Color } from 'three';
import { pixelRatio3d } from '@/editor/math/const';
import BaseModel3d from '../BaseModel3d';
import { ENTITY_RENDER_ORDER } from '@/editor/constant/entity';
import { LineSegments2, LineSegmentsGeometry } from 'three/examples/jsm/Addons.js';
import { getRotatedRectangleVertices } from '@/editor/math/utils';
import { IBoxesEntityParams } from '@/editor/types/entity';
import { isNumber } from 'lodash-es';

export class BoxesOutlineModel extends BaseModel3d {
  private material: LineMaterial = new LineMaterial({
    color: new Color(0x000000),
    depthTest: false,
    transparent: true,
    linewidth: 1
  });
  constructor() {
    super({ id: 'BoxesOutline' });

    globalStores.editorStateStore.sceneInfo.scene3d.scene.add(this.model);
  }

  createModel(params: IBoxesEntityParams) {
    const vertices: number[] = [];
    for (const box of params.boxes) {
      const [_lb, _rb, _rt, _lt] = getRotatedRectangleVertices(
        box.width,
        box.thickness,
        { x: box.x, y: box.y },
        box.rotation
      );

      const lb0 = [_lb.x / pixelRatio3d, _lb.y / pixelRatio3d, box.z / pixelRatio3d - box.height / pixelRatio3d / 2];
      const rb0 = [_rb.x / pixelRatio3d, _rb.y / pixelRatio3d, box.z / pixelRatio3d - box.height / pixelRatio3d / 2];
      const rt0 = [_rt.x / pixelRatio3d, _rt.y / pixelRatio3d, box.z / pixelRatio3d - box.height / pixelRatio3d / 2];
      const lt0 = [_lt.x / pixelRatio3d, _lt.y / pixelRatio3d, box.z / pixelRatio3d - box.height / pixelRatio3d / 2];
      const lb1 = [_lb.x / pixelRatio3d, _lb.y / pixelRatio3d, box.z / pixelRatio3d + box.height / pixelRatio3d / 2];
      const rb1 = [_rb.x / pixelRatio3d, _rb.y / pixelRatio3d, box.z / pixelRatio3d + box.height / pixelRatio3d / 2];
      const rt1 = [_rt.x / pixelRatio3d, _rt.y / pixelRatio3d, box.z / pixelRatio3d + box.height / pixelRatio3d / 2];
      const lt1 = [_lt.x / pixelRatio3d, _lt.y / pixelRatio3d, box.z / pixelRatio3d + box.height / pixelRatio3d / 2];

      vertices.push(
        ...lb0,
        ...rb0,
        ...rb0,
        ...rt0,
        ...rt0,
        ...lt0,
        ...lt0,
        ...lb0,
        ...lb1,
        ...rb1,
        ...rb1,
        ...rt1,
        ...rt1,
        ...lt1,
        ...lt1,
        ...lb1,
        ...lb0,
        ...lb1,
        ...rb0,
        ...rb1,
        ...rt0,
        ...rt1,
        ...lt0,
        ...lt1
      );
    }

    const color = new Color(params.color);

    const outlineGeometry = new LineSegmentsGeometry();
    outlineGeometry.setPositions(vertices);
    this.material.color = color;
    this.material.linewidth = params.linewidth;
    const outline = new LineSegments2(outlineGeometry, this.material);

    outline.renderOrder = ENTITY_RENDER_ORDER.selectedOutline;
    this.model.position.set(params.x / pixelRatio3d, params.y / pixelRatio3d, params.z / pixelRatio3d);
    this.model.rotation.z = params.rotation;

    this.model.add(outline);
  }

  update(params: Pick<IBoxesEntityParams, 'x' | 'y' | 'z' | 'rotation'>) {
    if (isNumber(params.x)) {
      this.model.position.x = params.x / pixelRatio3d;
    }
    if (isNumber(params.y)) {
      this.model.position.y = params.y / pixelRatio3d;
    }
    if (isNumber(params.z)) {
      this.model.position.z = params.z / pixelRatio3d;
    }
    if (isNumber(params.rotation)) {
      this.model.rotation.z = params.rotation;
    }
  }
}
